import React, { useState } from 'react';
import { Auth } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTheme } from '@mui/material';
import { Box } from '@mui/system';

import { Button, TextField, Typography } from '@zendesk/forethought-elements';
import { NewPasswordForm } from '../create-password-form/CreatePasswordForm';
import LandingPageLayout from 'src/components/reusable-components/landing-page-layout';
import { Routes } from 'src/utils/enums';
import { hasErrorMessage } from 'src/utils/typeGuards';

const ForgotUserPassword = () => {
  const { t } = useTranslation('authentication-wall');
  const theme = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isResetPasswordRequested, setIsResetPasswordRequested] =
    useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleEmailChange: React.ChangeEventHandler<
    HTMLInputElement
  > = event => {
    setEmail(event.currentTarget.value);
  };

  const handleGoBack = () => {
    navigate(Routes.HOMEPAGE);
  };

  return (
    <LandingPageLayout>
      {isResetPasswordRequested && (
        <NewPasswordForm
          errorMessage={errorMessage}
          handleSubmit={async ({ code = '', password1 }) => {
            setErrorMessage('');

            try {
              await Auth.forgotPasswordSubmit(email, code, password1);
              handleGoBack();
            } catch (error) {
              setErrorMessage(
                hasErrorMessage(error)
                  ? error.message
                  : t('common.genericError'),
              );
            }
          }}
          showCodeField
          title={t('forgotPassword.createNewPasswordTitle')}
        />
      )}

      {!isResetPasswordRequested && (
        <form
          onSubmit={async event => {
            event.preventDefault();

            setErrorMessage('');

            try {
              await Auth.forgotPassword(email);
              setIsResetPasswordRequested(true);
            } catch (error) {
              setErrorMessage(
                hasErrorMessage(error)
                  ? error.message
                  : t('common.genericError'),
              );
            }
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div>
              <Typography variant='font32'>
                {t('forgotPassword.title')}
              </Typography>
            </div>
            <div>
              <Typography variant='font16'>
                {t('forgotPassword.body')}
              </Typography>
            </div>
            <TextField
              aria-label={t('common.email')}
              name='username'
              onChange={handleEmailChange}
              placeholder={t('common.email')}
              required
              type='email'
              value={email}
            />
            {errorMessage && (
              <Box>
                <Typography
                  color={theme.palette.colors.red[500]}
                  variant='font16'
                >
                  {errorMessage}
                </Typography>
              </Box>
            )}
            <Button size='large' type='submit' variant='main'>
              {t('forgotPassword.resetPassword')}
            </Button>
            <Button onClick={handleGoBack} size='large' variant='secondary'>
              {t('forgotPassword.backToSignIn')}
            </Button>
          </Box>
        </form>
      )}
    </LandingPageLayout>
  );
};

export default ForgotUserPassword;
